import React, { useState } from 'react';
import FAQItem from '@ajat/design-system/FAQItem';
import { ComponentPage, DemoCard } from '../shared';

const ITEMS = [
  {
    id: 'delivery',
    question: 'Hvornår får vi vores tøj?',
    answer: 'Leveringen sker samlet til skolen cirka tre uger efter webshoppen lukker. I får en mail, når pakken er sendt.',
  },
  {
    id: 'sizes',
    question: 'Hvad hvis en størrelse ikke passer?',
    answer: 'Skriv til os inden for 14 dage, så bytter vi. Tøj med navnetryk kan desværre ikke byttes, da det er produceret specifikt til jer.',
  },
  {
    id: 'payment',
    question: 'Hvordan betaler vi?',
    answer: 'I kan betale med kort, MobilePay eller faktura. Klasserepræsentanten kan også vælge at samle betalingen ét sted.',
  },
];

export const meta = {
  component: FAQItem,
  title: 'FAQ Item',
  group: 'Content Blocks',
  importPath: '@ajat/design-system/FAQItem',
  description:
    'A single question-and-answer disclosure. It works uncontrolled for a simple list, or controlled when the page needs to own which rows are open — the HelpCenter FAQ pages keep a Set of expanded ids so deep links can open a specific question.',
  props: [
    ['question', 'string', '—', 'The question, always visible (required)'],
    ['answer', 'node', '—', 'The answer, revealed when expanded (required)'],
    ['expanded', 'bool', '—', 'Controlled open state; omit it for uncontrolled'],
    ['defaultExpanded', 'bool', 'false', 'Initial open state when uncontrolled'],
    ['onToggle', 'func', '—', 'Fires when the row is toggled'],
    ['id', 'string', '—', 'Id used for the aria wiring and as an anchor target'],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  usage: [
    {
      title: 'Controlled list — the HelpCenter pattern',
      code: `import FAQItem from '@ajat/design-system/FAQItem';

const [expanded, setExpanded] = useState(new Set());
const toggle = (id) => setExpanded((prev) => {
  const next = new Set(prev);
  next.has(id) ? next.delete(id) : next.add(id);
  return next;
});

{items.map((item) => (
  <FAQItem
    key={item.id}
    id={item.id}
    question={item.question}
    answer={item.answer}
    expanded={expanded.has(item.id)}
    onToggle={() => toggle(item.id)}
  />
))}`,
    },
    {
      title: 'Uncontrolled',
      code: `<FAQItem
  question="Hvornår får vi vores tøj?"
  answer="Leveringen sker samlet til skolen…"
  defaultExpanded
/>`,
    },
  ],
};

export default function FAQItemPage() {
  const [expanded, setExpanded] = useState(new Set(['delivery']));

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Controlled list" hint="several rows may be open at once">
        <div className="flex flex-col">
          {ITEMS.map((item) => (
            <FAQItem
              key={item.id}
              id={item.id}
              question={item.question}
              answer={item.answer}
              expanded={expanded.has(item.id)}
              onToggle={() => toggle(item.id)}
            />
          ))}
        </div>
      </DemoCard>

      <DemoCard title="Uncontrolled" hint="each row owns its own state">
        <div className="flex flex-col">
          <FAQItem question={ITEMS[0].question} answer={ITEMS[0].answer} defaultExpanded />
          <FAQItem question={ITEMS[1].question} answer={ITEMS[1].answer} />
        </div>
      </DemoCard>

      <DemoCard title="Rich answer" hint="answer takes a node, not just a string">
        <FAQItem
          id="rich"
          question="Hvad kan vi få trykt?"
          defaultExpanded
          answer={(
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-ui-small-l)' }}>
              <p className="typo-body">I kan vælge mellem:</p>
              <ul style={{ paddingLeft: '1.2em', listStyle: 'disc' }}>
                <li className="typo-body">Navnetryk på brystet</li>
                <li className="typo-body">Klassens bogstav på ærmet</li>
                <li className="typo-body">Rygtryk i A4 eller A3</li>
              </ul>
            </div>
          )}
        />
      </DemoCard>
    </ComponentPage>
  );
}
